import { useState } from 'react';
import { Save, Star } from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import type { Pipeline } from '@/types/pipeline';

interface GeneralTabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
  /** Refetch the pipeline list (used after a default change so the list reflects it). */
  onRefresh?: () => void;
}

export default function GeneralTab({ pipeline, onUpdate, onRefresh }: GeneralTabProps) {
  const [formData, setFormData] = useState({
    name: pipeline.name,
    description: pipeline.description || '',
    isDefault: pipeline.isDefault,
    isActive: pipeline.isActive,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Pipeline name is required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const updated = await pipelineService.updatePipeline(pipeline.id, {
        name: formData.name,
        description: formData.description || undefined,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
      });
      onUpdate({ ...pipeline, ...updated });
      setHasChanges(false);
      // A default change flips another pipeline's flag — refresh the list.
      onRefresh?.();
    } catch (err: any) {
      console.error('Error updating pipeline:', err);
      setError(err?.response?.data?.message || 'Failed to update pipeline');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Basic Information</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Pipeline Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Sales Pipeline, Support Pipeline"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the purpose of this pipeline..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Settings</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <Star className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Default Pipeline</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  New tickets will be assigned to this pipeline by default
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => handleChange('isDefault', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg dark:bg-gray-900">
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Active</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Inactive pipelines won't accept new tickets
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
            </label>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
